/**
 * Frontend Logger Utility
 *
 * Centralized logging for the web application with environment-aware filtering
 * and structured logging support for future observability integration.
 *
 * Usage:
 *   import { logger } from './utils/logger';
 *
 *   logger.debug('Component rendered', { componentName: 'Dashboard' });
 *   logger.info('User action', { action: 'click', target: 'button' });
 *   logger.warn('Deprecated API used', { api: 'oldMethod' });
 *   logger.error('Failed to load data', { error: err.message });
 *
 * Log Levels:
 *   - debug: Development-only detailed logs (filtered in production)
 *   - info: Informational messages (filtered in production)
 *   - warn: Warning messages (shown in all environments)
 *   - error: Error messages (shown in all environments)
 */

export enum LogLevel {
  DEBUG = 'debug',
  INFO = 'info',
  WARN = 'warn',
  ERROR = 'error',
}

export interface LogMetadata {
  [key: string]: unknown;
}

export interface LogEntry {
  timestamp: string;
  level: LogLevel;
  message: string;
  context?: string;
  metadata?: LogMetadata;
  environment: string;
}

class FrontendLogger {
  private environment: string;
  private isProduction: boolean;
  private observers: ((entry: LogEntry) => void)[] = [];

  constructor() {
    this.environment = process.env.NODE_ENV || 'development';
    this.isProduction = this.environment === 'production';
  }

  /**
   * Register an observer for all log entries (for observability tools)
   * Returns an unsubscribe function
   */
  public observe(callback: (entry: LogEntry) => void): () => void {
    this.observers.push(callback);
    return () => {
      const index = this.observers.indexOf(callback);
      if (index > -1) {
        this.observers.splice(index, 1);
      }
    };
  }

  /**
   * Notify all observers of a log entry
   */
  private notify(entry: LogEntry): void {
    this.observers.forEach((observer) => {
      try {
        observer(entry);
      } catch (error) {
        // Prevent observer errors from breaking logging
        console.error('Logger observer error:', error);
      }
    });
  }

  /**
   * Create a structured log entry
   */
  private createEntry(
    level: LogLevel,
    message: string,
    context?: string,
    metadata?: LogMetadata,
  ): LogEntry {
    return {
      timestamp: new Date().toISOString(),
      level,
      message,
      context,
      metadata,
      environment: this.environment,
    };
  }

  /**
   * Format log entry for console output
   */
  private formatConsoleMessage(entry: LogEntry): unknown[] {
    const prefix = entry.context ? `[${entry.context}]` : '';
    const baseMessage = `${prefix} ${entry.message}`;

    if (entry.metadata && Object.keys(entry.metadata).length > 0) {
      return [baseMessage, entry.metadata];
    }

    return [baseMessage];
  }

  /**
   * Debug-level logging (development only)
   * Filtered out in production
   */
  public debug(
    message: string,
    metadataOrContext?: LogMetadata | string,
    metadata?: LogMetadata,
  ): void {
    if (this.isProduction) {
      return; // Skip debug logs in production
    }

    const context = typeof metadataOrContext === 'string' ? metadataOrContext : undefined;
    const meta = typeof metadataOrContext === 'object' ? metadataOrContext : metadata;

    const entry = this.createEntry(LogLevel.DEBUG, message, context, meta);
    const consoleArgs = this.formatConsoleMessage(entry);

    console.info(...consoleArgs);
    this.notify(entry);
  }

  /**
   * Info-level logging (development only)
   * Filtered out in production
   */
  public info(
    message: string,
    metadataOrContext?: LogMetadata | string,
    metadata?: LogMetadata,
  ): void {
    if (this.isProduction) {
      return; // Skip info logs in production
    }

    const context = typeof metadataOrContext === 'string' ? metadataOrContext : undefined;
    const meta = typeof metadataOrContext === 'object' ? metadataOrContext : metadata;

    const entry = this.createEntry(LogLevel.INFO, message, context, meta);
    const consoleArgs = this.formatConsoleMessage(entry);

    console.info(...consoleArgs);
    this.notify(entry);
  }

  /**
   * Warning-level logging (shown in all environments)
   */
  public warn(
    message: string,
    metadataOrContext?: LogMetadata | string,
    metadata?: LogMetadata,
  ): void {
    const context = typeof metadataOrContext === 'string' ? metadataOrContext : undefined;
    const meta = typeof metadataOrContext === 'object' ? metadataOrContext : metadata;

    const entry = this.createEntry(LogLevel.WARN, message, context, meta);
    const consoleArgs = this.formatConsoleMessage(entry);

    console.warn(...consoleArgs);
    this.notify(entry);
  }

  /**
   * Error-level logging (shown in all environments)
   */
  public error(
    message: string,
    metadataOrContext?: LogMetadata | string,
    metadata?: LogMetadata,
  ): void {
    const context = typeof metadataOrContext === 'string' ? metadataOrContext : undefined;
    const meta = typeof metadataOrContext === 'object' ? metadataOrContext : metadata;

    const entry = this.createEntry(LogLevel.ERROR, message, context, meta);
    const consoleArgs = this.formatConsoleMessage(entry);

    console.error(...consoleArgs);
    this.notify(entry);
  }

  /**
   * Get current environment
   */
  public getEnvironment(): string {
    return this.environment;
  }

  /**
   * Check if running in production
   */
  public isProductionEnvironment(): boolean {
    return this.isProduction;
  }
}

// Export singleton instance
export const logger = new FrontendLogger();

// Export class for testing
export { FrontendLogger };
